"use client";

/**
 * Component Demo Page - Showcases rich content viewers in a chat-like context
 *
 * Demonstrates all rich content features without requiring the full runtime:
 * - ExecutionLog with subagents and tool calls
 * - MarkdownText with Mermaid diagrams
 * - JsonDisplay with status badges
 * - CodeViewer with syntax highlighting
 * - CSVViewer for tabular data
 */

import { useState, type FC } from "react";
import { ExecutionLog, type ExecutionEvent } from "./execution-log";
import { MarkdownText } from "./markdown-text";
import { JsonDisplay } from "./json-display";
import { CodeViewer, CSVViewer } from "./file-viewer";
import { ResourceListMessage, type ResourceListPayload } from "./resource-list-message";
import { UserInputPrompt } from "./user-input-prompt";
import type { WaitingForUserInput } from "@/lib/useEurekaRuntime";
import { ThemeProvider, useTheme } from "@/lib/theme-context";
import { cn } from "@/lib/utils";
import { SunIcon, MoonIcon, Bot, User, ChevronDownIcon, ChevronRightIcon } from "lucide-react";

// =============================================================================
// DEMO DATA
// =============================================================================

const MERMAID_ARCHITECTURE = `flowchart TD
    A([User Query]) --> B[Explorer Agent]
    B --> C{Needs Data?}
    C -->|Yes| D[Query Executor]
    C -->|No| E[Direct Response]
    D --> F[Database]
    F --> G[Results Processing]
    G --> H[Schema Analyzer]
    H --> I([Final Response])
    E --> I`;

const MERMAID_SEQUENCE = `sequenceDiagram
    participant U as User
    participant E as Explorer
    participant Q as QueryExecutor
    participant S as SchemaAnalyzer
    participant DB as Database

    U->>E: "Show top customers"
    E->>S: Analyze schema
    S-->>E: Found customers, orders tables
    E->>Q: Execute SQL
    Q->>DB: SELECT query
    DB-->>Q: Results
    Q-->>E: Formatted data
    E-->>U: Response with table`;

const MERMAID_ER = `erDiagram
    CUSTOMERS ||--o{ ORDERS : places
    CUSTOMERS {
        uuid customer_id PK
        string name
        string region
        timestamp created_at
    }
    ORDERS ||--|{ ORDER_ITEMS : contains
    ORDERS {
        uuid order_id PK
        uuid customer_id FK
        decimal total_amount
        date order_date
    }
    ORDER_ITEMS {
        uuid item_id PK
        uuid order_id FK
        string product_name
        int quantity
        decimal unit_price
    }`;

const SQL_QUERY = `-- Generated by Query Executor
WITH customer_stats AS (
    SELECT
        c.customer_id,
        c.name,
        c.region,
        COUNT(o.order_id) as total_orders,
        SUM(o.total_amount) as revenue
    FROM customers c
    LEFT JOIN orders o ON c.customer_id = o.customer_id
    WHERE o.order_date >= '2024-01-01'
    GROUP BY c.customer_id, c.name, c.region
)
SELECT
    name,
    region,
    total_orders,
    revenue,
    RANK() OVER (ORDER BY revenue DESC) as rank
FROM customer_stats
ORDER BY revenue DESC
LIMIT 10;`;

const PYTHON_SCHEMA = `# Schema analysis results
from dataclasses import dataclass
from typing import List

@dataclass
class TableSchema:
    name: str
    columns: List[dict]
    row_count: int
    relationships: List[str]

# Discovered schema
customers = TableSchema(
    name="customers",
    columns=[
        {"name": "customer_id", "type": "UUID", "primary_key": True},
        {"name": "name", "type": "VARCHAR(255)"},
        {"name": "region", "type": "VARCHAR(50)"},
        {"name": "created_at", "type": "TIMESTAMP"},
    ],
    row_count=15420,
    relationships=["orders.customer_id"]
)`;

const CSV_RESULTS = `Customer,Region,Orders,Revenue,Rank
Acme Corp,North America,156,$2.4M,1
TechStart Inc,Europe,134,$1.9M,2
Global Systems,Asia Pacific,198,$1.7M,3
DataFlow Ltd,North America,89,$1.5M,4
CloudNine Co,Europe,112,$1.3M,5
Innovate LLC,Asia Pacific,145,$1.2M,6
Prime Solutions,North America,78,$980K,7
NextGen Tech,Europe,92,$870K,8
Alpha Industries,Asia Pacific,67,$750K,9
Beta Corp,North America,54,$680K,10`;

// Markdown responses
const RESPONSE_1 = `I'll analyze the database schema and fetch the top customers for you. Let me delegate to my specialized subagents.`;

const RESPONSE_2 = `## Top 10 Customers by Revenue (2024)

Here are your top performing customers:

| Rank | Customer | Region | Orders | Revenue |
|------|----------|--------|--------|---------|
| 1 | Acme Corp | North America | 156 | $2.4M |
| 2 | TechStart Inc | Europe | 134 | $1.9M |
| 3 | Global Systems | Asia Pacific | 198 | $1.7M |
| 4 | DataFlow Ltd | North America | 89 | $1.5M |
| 5 | CloudNine Co | Europe | 112 | $1.3M |

**Key Insights:**
- **North America** leads with 4 of top 10 customers
- **Acme Corp** has the highest revenue at $2.4M
- **Global Systems** has the most orders (198) but ranks 3rd in revenue

> The data is aggregated from orders placed since January 1, 2024.`;

const RESPONSE_3 = `Here's the architecture showing how I processed your query:

\`\`\`mermaid
${MERMAID_ARCHITECTURE}
\`\`\`

And the sequence of operations:

\`\`\`mermaid
${MERMAID_SEQUENCE}
\`\`\`

### How the Query Was Processed

1. **Explorer Agent** received your natural language query
2. **Schema Analyzer** (subagent) discovered the \`customers\` and \`orders\` tables
3. **Query Executor** (subagent) generated optimized SQL with:
   - CTE for aggregation
   - Window function for ranking
   - Index hint for performance
4. Results were formatted and returned

The total processing took **~450ms** with **2.1k tokens** used across both subagents.`;

// Sample resource list data for ResourceListMessage demo
const RESOURCE_LIST_PAYLOAD: ResourceListPayload = {
  type: "resource_list",
  resources: [
    { source_id: "1", source_name: "Production DB", source_type: "PostgreSQL", connection_type: "resources.database.PostgreSQL", scope: "production", created_at: "2024-01-15T10:30:00Z" },
    { source_id: "2", source_name: "Analytics Warehouse", source_type: "Snowflake", connection_type: "resources.database.Snowflake", scope: "analytics", created_at: "2024-02-20T14:45:00Z" },
    { source_id: "3", source_name: "User Events", source_type: "Kafka", connection_type: "resources.streaming.Kafka", scope: "production", created_at: "2024-03-10T09:15:00Z" },
    { source_id: "4", source_name: "ML Features", source_type: "Redis", connection_type: "resources.cache.Redis", scope: "ml-platform", created_at: "2024-01-25T16:20:00Z" },
    { source_id: "5", source_name: "Customer Data", source_type: "PostgreSQL", connection_type: "resources.database.PostgreSQL", scope: "production", created_at: "2024-04-05T11:00:00Z" },
    { source_id: "6", source_name: "Logs Storage", source_type: "S3", connection_type: "resources.storage.S3", scope: "observability", created_at: "2024-02-28T08:30:00Z" },
    { source_id: "7", source_name: "Dev Database", source_type: "PostgreSQL", connection_type: "resources.database.PostgreSQL", scope: "development", created_at: "2024-03-15T13:45:00Z" },
    { source_id: "8", source_name: "Session Cache", source_type: "Redis", connection_type: "resources.cache.Redis", scope: "production", created_at: "2024-04-12T10:10:00Z" },
  ],
  metadata: {
    total_count: 8,
    grouped_by_type: { PostgreSQL: 3, Redis: 2, Snowflake: 1, Kafka: 1, S3: 1 }
  }
};

const RESPONSE_4 = `Here's the entity relationship diagram for the database:

\`\`\`mermaid
${MERMAID_ER}
\`\`\`

The schema has **3 tables** with proper foreign key relationships. The \`customers\` table is the root, with \`orders\` containing customer references and \`order_items\` for line-item details.`;

// =============================================================================
// USER INPUT PROMPT EXAMPLES - Used within agent flow context
// =============================================================================

const USER_INPUT_FREE_TEXT: WaitingForUserInput = {
  question: "I need a custom date range for the analysis. Please provide the start date in YYYY-MM-DD format:",
  inputType: "free_text",
  timeoutAt: new Date(Date.now() + 300000).toISOString(),
};

const USER_INPUT_SINGLE_SELECT: WaitingForUserInput = {
  question: "I found multiple database connections. Which one should I use for this analysis?",
  inputType: "single_select",
  options: ["Production DB (PostgreSQL)", "Staging DB (PostgreSQL)", "Analytics Warehouse (Snowflake)", "Development DB (SQLite)"],
  timeoutAt: new Date(Date.now() + 600000).toISOString(),
};

const USER_INPUT_MULTI_SELECT: WaitingForUserInput = {
  question: "I can analyze the following tables. Select all that should be included:",
  inputType: "multi_select",
  options: ["customers", "orders", "order_items", "products", "inventory", "shipping"],
  timeoutAt: new Date(Date.now() + 600000).toISOString(),
};

const USER_INPUT_CONFIRMATION: WaitingForUserInput = {
  question: "This query will scan approximately 2.5 million rows and may take 30-60 seconds. The estimated cost is $0.15. Do you want to proceed?",
  inputType: "confirmation",
  timeoutAt: new Date(Date.now() + 120000).toISOString(),
};

// Complex form schema for database connection
const USER_INPUT_RESOURCE_FORM: WaitingForUserInput = {
  question: "Please configure the new database connection:",
  inputType: "resource_form",
  formAction: "create",
  resourceType: "resources.database.PostgreSQL",
  formSchema: {
    type: "resources.database.PostgreSQL",
    display_name: "PostgreSQL Connection",
    description: "Configure a new PostgreSQL database connection",
    category: "Database",
    fields: [
      {
        name: "name",
        display_name: "Connection Name",
        type: "string",
        required: true,
        placeholder: "e.g., Production Analytics DB",
        description: "A friendly name for this connection"
      },
      {
        name: "host",
        display_name: "Host",
        type: "string",
        required: true,
        placeholder: "localhost or hostname",
        description: "Database server hostname or IP"
      },
      {
        name: "port",
        display_name: "Port",
        type: "integer",
        required: true,
        default: 5432,
        description: "Database server port"
      },
      {
        name: "database",
        display_name: "Database Name",
        type: "string",
        required: true,
        placeholder: "mydb",
        description: "Name of the database to connect to"
      },
      {
        name: "username",
        display_name: "Username",
        type: "string",
        required: true,
        placeholder: "postgres"
      },
      {
        name: "password",
        display_name: "Password",
        type: "secret",
        required: true,
        placeholder: "••••••••"
      },
      {
        name: "ssl_mode",
        display_name: "SSL Mode",
        type: "dropdown",
        required: false,
        default: "prefer",
        options: [
          { label: "Disable", value: "disable" },
          { label: "Allow", value: "allow" },
          { label: "Prefer", value: "prefer" },
          { label: "Require", value: "require" },
          { label: "Verify CA", value: "verify-ca" },
          { label: "Verify Full", value: "verify-full" }
        ]
      },
      {
        name: "connection_timeout",
        display_name: "Connection Timeout (seconds)",
        type: "integer",
        required: false,
        default: 30,
        description: "Maximum time to wait for connection"
      }
    ],
    agent_guidance: {
      required_steps: [
        "Enter connection name and host details",
        "Provide database credentials",
        "Configure SSL mode for security",
        "Test the connection before saving"
      ],
      common_errors: [],
      best_practices: []
    }
  },
  formValues: {},
  timeoutAt: new Date(Date.now() + 600000).toISOString(),
};

// =============================================================================
// MOCK EXECUTION EVENTS
// =============================================================================

const createMockEvents = (): ExecutionEvent[] => {
  const baseTime = Date.now() - 300000;

  return [
    // Subagent: Schema Analyzer
    {
      type: "subagent_started",
      execution_id: "exec-schema-1",
      parent_execution_id: "exec-root-1",
      messageId: "msg-1",
      agent_type: "Schema Analyzer",
      goal: "Discover relevant tables and relationships for customer revenue query",
      timestamp: baseTime + 2000,
    },
    {
      type: "tool_call_started",
      execution_id: "exec-schema-1",
      messageId: "msg-1",
      tool_use_id: "tool-1",
      tool_name: "analyze_schema",
      tool_input: { query: "customer revenue", include_relationships: true },
      is_async: false,
      timestamp: baseTime + 2500,
    },
    {
      type: "tool_call_completed",
      execution_id: "exec-schema-1",
      messageId: "msg-1",
      tool_use_id: "tool-1",
      tool_name: "analyze_schema",
      result: {
        tables_found: 3,
        tables: ["customers", "orders", "order_items"],
        relationships: 5,
        schema_code: PYTHON_SCHEMA,
        assessment: {
          score: 4,
          explanation: "Well-structured schema with proper indexes and foreign keys",
          evidence: [
            "Primary keys on all tables",
            "Foreign key constraints enforced",
            "Revenue index available for fast aggregation"
          ]
        }
      },
      is_error: false,
      duration_ms: 156,
      timestamp: baseTime + 2656,
    },
    {
      type: "subagent_completed",
      execution_id: "exec-schema-1",
      parent_execution_id: "exec-root-1",
      messageId: "msg-1",
      duration_ms: 800,
      total_input_tokens: 450,
      total_output_tokens: 780,
      cost_usd: 0.0012,
      timestamp: baseTime + 3000,
    },

    // Subagent: Query Executor
    {
      type: "subagent_started",
      execution_id: "exec-query-1",
      parent_execution_id: "exec-root-1",
      messageId: "msg-1",
      agent_type: "Query Executor",
      goal: "Build and execute SQL query for top 10 customers by revenue",
      timestamp: baseTime + 3500,
    },
    {
      type: "tool_call_started",
      execution_id: "exec-query-1",
      messageId: "msg-1",
      tool_use_id: "tool-2",
      tool_name: "generate_sql",
      tool_input: {
        intent: "top 10 customers by revenue",
        tables: ["customers", "orders"],
        time_filter: "2024-01-01"
      },
      is_async: false,
      timestamp: baseTime + 4000,
    },
    {
      type: "tool_call_completed",
      execution_id: "exec-query-1",
      messageId: "msg-1",
      tool_use_id: "tool-2",
      tool_name: "generate_sql",
      result: {
        sql: SQL_QUERY,
        estimated_cost: "low",
        uses_index: true
      },
      is_error: false,
      duration_ms: 89,
      timestamp: baseTime + 4089,
    },
    {
      type: "tool_call_started",
      execution_id: "exec-query-1",
      messageId: "msg-1",
      tool_use_id: "tool-3",
      tool_name: "execute_sql",
      tool_input: { sql: SQL_QUERY, timeout_ms: 30000 },
      is_async: false,
      timestamp: baseTime + 4200,
    },
    {
      type: "tool_call_completed",
      execution_id: "exec-query-1",
      messageId: "msg-1",
      tool_use_id: "tool-3",
      tool_name: "execute_sql",
      result: {
        status: "success",
        rows_returned: 10,
        execution_time_ms: 234,
        query_plan: "Index Scan on customers_revenue_idx",
        data: {
          type: "file",
          filename: "query_results.csv",
          mime_type: "text/csv",
          content: CSV_RESULTS,
          size: 412
        }
      },
      is_error: false,
      duration_ms: 234,
      timestamp: baseTime + 4434,
    },
    {
      type: "subagent_completed",
      execution_id: "exec-query-1",
      parent_execution_id: "exec-root-1",
      messageId: "msg-1",
      duration_ms: 1200,
      total_input_tokens: 320,
      total_output_tokens: 570,
      cost_usd: 0.0009,
      timestamp: baseTime + 5000,
    },

    // Error example
    {
      type: "tool_call_started",
      execution_id: "exec-root-1",
      messageId: "msg-error",
      tool_use_id: "tool-error",
      tool_name: "execute_sql",
      tool_input: {
        sql: "SELECT * FROM nonexistent_table",
        timeout_ms: 30000
      },
      is_async: false,
      timestamp: baseTime + 186000,
    },
    {
      type: "tool_call_completed",
      execution_id: "exec-root-1",
      messageId: "msg-error",
      tool_use_id: "tool-error",
      tool_name: "execute_sql",
      result: {
        status: "error",
        message: "Table 'nonexistent_table' does not exist",
        error_code: "TABLE_NOT_FOUND",
        suggestion: "Did you mean 'customers' or 'orders'?",
        details: {
          available_tables: ["customers", "orders", "order_items", "products"]
        }
      },
      is_error: true,
      duration_ms: 12,
      timestamp: baseTime + 186012,
    },
  ];
};

// =============================================================================
// COMPONENTS
// =============================================================================

const ThemeToggle: FC = () => {
  const { resolvedTheme, setTheme } = useTheme();

  return (
    <button
      onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm",
        "bg-muted hover:bg-muted/80 text-foreground"
      )}
    >
      {resolvedTheme === "dark" ? (
        <>
          <SunIcon className="h-4 w-4" />
          <span>Light</span>
        </>
      ) : (
        <>
          <MoonIcon className="h-4 w-4" />
          <span>Dark</span>
        </>
      )}
    </button>
  );
};

interface MessageBubbleProps {
  role: "user" | "assistant";
  children: React.ReactNode;
}

const MessageBubble: FC<MessageBubbleProps> = ({ role, children }) => (
  <div className={cn("flex gap-3 py-3", role === "user" ? "flex-row-reverse" : "")}>
    <div className={cn(
      "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
      role === "user" ? "bg-blue-500" : "bg-emerald-500"
    )}>
      {role === "user" ? (
        <User className="h-4 w-4 text-white" />
      ) : (
        <Bot className="h-4 w-4 text-white" />
      )}
    </div>
    <div className={cn("flex-1 min-w-0", role === "user" ? "text-right" : "")}>
      {children}
    </div>
  </div>
);

interface SectionProps {
  title: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

const Section: FC<SectionProps> = ({ title, defaultExpanded = true, children }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/50 transition-colors"
      >
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="font-medium text-foreground">{title}</span>
      </button>
      {expanded && (
        <div className="border-t border-border p-4">
          {children}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN DEMO
// =============================================================================

const DemoContent: FC = () => {
  const events = createMockEvents();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-foreground text-lg">Explorer Agent</h1>
              <p className="text-xs text-muted-foreground">
                Rich Content Demo - All Components
              </p>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* Section 1: Execution Log with Subagents */}
        <Section title="1. Execution Log with Subagents & Tool Calls">
          <MessageBubble role="user">
            <div className="inline-block bg-blue-500 text-white px-4 py-2 rounded-2xl rounded-tr-sm">
              Show me the top 10 customers by revenue for this year
            </div>
          </MessageBubble>

          <MessageBubble role="assistant">
            <div className="space-y-3">
              <MarkdownText text={RESPONSE_1} />
              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                <ExecutionLog
                  events={events.filter(e => e.messageId === "msg-1")}
                  isRunning={false}
                />
              </div>
              <MarkdownText text={RESPONSE_2} />
            </div>
          </MessageBubble>
        </Section>

        {/* Section 2: Mermaid Diagrams */}
        <Section title="2. Mermaid Diagrams (Flowchart, Sequence, ER)">
          <MessageBubble role="user">
            <div className="inline-block bg-blue-500 text-white px-4 py-2 rounded-2xl rounded-tr-sm">
              Show me the architecture and sequence diagrams
            </div>
          </MessageBubble>

          <MessageBubble role="assistant">
            <MarkdownText text={RESPONSE_3} />
          </MessageBubble>

          <MessageBubble role="user">
            <div className="inline-block bg-blue-500 text-white px-4 py-2 rounded-2xl rounded-tr-sm">
              Show me the database ER diagram
            </div>
          </MessageBubble>

          <MessageBubble role="assistant">
            <MarkdownText text={RESPONSE_4} />
          </MessageBubble>
        </Section>

        {/* Section 3: Code Viewers */}
        <Section title="3. Syntax Highlighted Code (Python, SQL, JSON)">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Python Schema Analysis:</p>
              <CodeViewer content={PYTHON_SCHEMA} language="python" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">Generated SQL Query:</p>
              <CodeViewer content={SQL_QUERY} language="sql" />
            </div>
          </div>
        </Section>

        {/* Section 4: CSV Viewer */}
        <Section title="4. CSV Table Viewer (Basic)">
          <p className="text-sm text-muted-foreground mb-3">Query results displayed as a basic table:</p>
          <CSVViewer content={CSV_RESULTS} />
        </Section>

        {/* Section 4b: Resource List with Sorting & Search */}
        <Section title="4b. Data Table with Sorting, Search & Filters">
          <p className="text-sm text-muted-foreground mb-3">
            Interactive table with column sorting, search, and type filtering (click headers to sort):
          </p>
          <ResourceListMessage
            payload={RESOURCE_LIST_PAYLOAD}
            onView={(id) => console.log("View:", id)}
            onEdit={(id) => console.log("Edit:", id)}
            onDelete={(id) => console.log("Delete:", id)}
          />
        </Section>

        {/* Section 5: JSON Display */}
        <Section title="5. JSON Display with Status Badges & Scores">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <p className="text-sm text-muted-foreground mb-3">Success Response:</p>
              <JsonDisplay data={{
                status: "success",
                rows_returned: 10,
                execution_time_ms: 234,
                query_plan: "Index Scan on customers_revenue_idx",
                model: "claude-sonnet-4-20250514"
              }} />
            </div>
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <p className="text-sm text-muted-foreground mb-3">Assessment with Score:</p>
              <JsonDisplay data={{
                assessment: {
                  score: 4,
                  explanation: "Well-structured schema with proper indexes and foreign keys",
                  evidence: [
                    "Primary keys on all tables",
                    "Foreign key constraints enforced",
                    "Revenue index available"
                  ]
                }
              }} />
            </div>
          </div>
        </Section>

        {/* Section 6: User Input Prompts - Shown in Agent Flow Context */}
        <Section title="6. User Input Prompts (Ask User) - In Agent Flow">
          <p className="text-sm text-muted-foreground mb-4">
            Agents can pause execution to request user input. Here's a realistic flow showing all 5 input types:
          </p>

          {/* User starts the conversation */}
          <MessageBubble role="user">
            <div className="inline-block bg-blue-500 text-white px-4 py-2 rounded-2xl rounded-tr-sm">
              Set up a new database connection and run a customer analysis
            </div>
          </MessageBubble>

          {/* Agent responds and asks for database selection */}
          <MessageBubble role="assistant">
            <div className="space-y-3">
              <MarkdownText text="I'll help you set up a new database connection and run the analysis. Let me first check what's available..." />
              <UserInputPrompt
                waitingForUserInput={USER_INPUT_SINGLE_SELECT}
                onSubmit={(response) => console.log("Single select:", response)}
              />
            </div>
          </MessageBubble>

          {/* Simulated user response */}
          <MessageBubble role="user">
            <div className="inline-block bg-blue-500 text-white px-4 py-2 rounded-2xl rounded-tr-sm">
              Production DB (PostgreSQL)
            </div>
          </MessageBubble>

          {/* Agent asks for tables */}
          <MessageBubble role="assistant">
            <div className="space-y-3">
              <MarkdownText text="Connected to Production DB. I found several tables that might be relevant for customer analysis:" />
              <UserInputPrompt
                waitingForUserInput={USER_INPUT_MULTI_SELECT}
                onSubmit={(response) => console.log("Multi select:", response)}
              />
            </div>
          </MessageBubble>

          {/* Simulated user response */}
          <MessageBubble role="user">
            <div className="inline-block bg-blue-500 text-white px-4 py-2 rounded-2xl rounded-tr-sm">
              customers, orders, order_items
            </div>
          </MessageBubble>

          {/* Agent asks for date range */}
          <MessageBubble role="assistant">
            <div className="space-y-3">
              <MarkdownText text="I'll analyze those 3 tables. For the time-based analysis, I need a custom date range:" />
              <UserInputPrompt
                waitingForUserInput={USER_INPUT_FREE_TEXT}
                onSubmit={(response) => console.log("Free text:", response)}
              />
            </div>
          </MessageBubble>

          {/* Simulated user response */}
          <MessageBubble role="user">
            <div className="inline-block bg-blue-500 text-white px-4 py-2 rounded-2xl rounded-tr-sm">
              2024-01-01
            </div>
          </MessageBubble>

          {/* Agent asks for confirmation before expensive query */}
          <MessageBubble role="assistant">
            <div className="space-y-3">
              <MarkdownText text="I've built the analysis query. Before I run it, I need your confirmation:" />
              <UserInputPrompt
                waitingForUserInput={USER_INPUT_CONFIRMATION}
                onSubmit={(response) => console.log("Confirmation:", response)}
              />
            </div>
          </MessageBubble>

          {/* Simulated user response */}
          <MessageBubble role="user">
            <div className="inline-block bg-blue-500 text-white px-4 py-2 rounded-2xl rounded-tr-sm">
              Yes
            </div>
          </MessageBubble>

          {/* Agent shows results and asks for new connection setup */}
          <MessageBubble role="assistant">
            <div className="space-y-3">
              <MarkdownText text="Analysis complete! Now, you mentioned setting up a **new** database connection. Please fill in the connection details:" />
              <UserInputPrompt
                waitingForUserInput={USER_INPUT_RESOURCE_FORM}
                onSubmit={(response) => console.log("Resource form:", response)}
              />
            </div>
          </MessageBubble>
        </Section>

        {/* Section 7: Error Handling */}
        <Section title="7. Error Handling in Tool Calls">
          <MessageBubble role="user">
            <div className="inline-block bg-blue-500 text-white px-4 py-2 rounded-2xl rounded-tr-sm">
              What happens if there's an error?
            </div>
          </MessageBubble>

          <MessageBubble role="assistant">
            <div className="space-y-3">
              <p className="text-foreground">
                When a tool fails, you'll see detailed error information:
              </p>
              <div className="bg-muted/30 rounded-lg p-3 border border-border">
                <ExecutionLog
                  events={events.filter(e => e.messageId === "msg-error")}
                  isRunning={false}
                />
              </div>
              <JsonDisplay data={{
                status: "error",
                message: "Table 'nonexistent_table' does not exist",
                error_code: "TABLE_NOT_FOUND",
                suggestion: "Did you mean 'customers' or 'orders'?",
                details: {
                  available_tables: ["customers", "orders", "order_items", "products"]
                }
              }} />
            </div>
          </MessageBubble>
        </Section>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground pt-6 pb-4 border-t border-border">
          <p className="font-medium">Eureka AI Assistant - Component Demo</p>
          <p className="text-xs mt-1">
            ExecutionLog • Mermaid Diagrams • Syntax Highlighting • Data Tables • User Input Prompts • JSON Display
          </p>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// EXPORT
// =============================================================================

export const ComponentDemoPage: FC = () => {
  return (
    <ThemeProvider>
      <DemoContent />
    </ThemeProvider>
  );
};

export default ComponentDemoPage;
